import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { metaForPath, SITE_URL } from './routeMeta';

/**
 * Keeps <title>, the meta description and the canonical/OG tags in step with
 * the current route during client-side navigation.
 *
 * This is the runtime half of the fix. The build-time half — real static HTML
 * per route, written by scripts/prerender-meta.mjs — is what serves crawlers
 * that never run JavaScript. Both read the same ROUTE_META table, so they
 * cannot disagree.
 */
export function useDocumentMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    const canonical = `${SITE_URL}${meta.path === '/' ? '/' : meta.path}`;

    document.title = meta.title;

    const setTag = (selector: string, attr: string, value: string) => {
      const el = document.head.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setTag('meta[name="description"]', 'content', meta.description);
    setTag('meta[property="og:title"]', 'content', meta.title);
    setTag('meta[property="og:description"]', 'content', meta.description);
    setTag('meta[property="og:url"]', 'content', canonical);
    setTag('meta[name="twitter:title"]', 'content', meta.title);
    setTag('meta[name="twitter:description"]', 'content', meta.description);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }, [pathname]);
}
